import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../layouts/PageLayout';
import BackgroundSection from 'components/BackgroundSection';
import Brochure from 'components/Brochure';
import TextPage from 'components/TextPage';
import HeroSection from 'components/HeroSection';
import { getMainServicesData } from 'api-sanity/api';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import RangeCarousel from 'components/RangeCarousel';
import FullPageImageAnimation from 'components/FullPageImageAnimation';
import useMediaQuery from 'hooks/useMediaQuery';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '5728ce02-3c95-4871-854c-c96311629731',
  P_2: '3f211b23-ef38-4daa-a217-10e9a36b9915',
};

const mainServicesIds = {
  heroId: '8ec14f27-980f-42fd-b4b9-19fca6e0139a',
  ourRangeId: 'a10d9be4-bbba-4094-985d-bac29aa1978e',
  brochureId: '705fa18e-fd9e-4389-b0bc-0a755cb811b3',
  fullPageImageId: 'c1c125bf-f662-42f2-83b3-7bb7bae49f20',
};

const MainServices = () => {
  const [mainServicesData, setMainServicesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();
  const isMobileSize = useMediaQuery();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('services');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getMainServicesData(mainServicesIds, onProgress)
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setMainServicesData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!mainServicesData?.textPages) return null;
    return mainServicesData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout className='main-services-page'>
      <HeroSection
        imageUrl={
          isDarkMode
            ? mainServicesData?.heroSection?.darkModeImageUrl ||
              mainServicesData?.heroSection?.lightModeImageUrl
            : mainServicesData?.heroSection?.lightModeImageUrl
        }
        title={mainServicesData?.heroSection?.title}
        titleHighlight={mainServicesData?.heroSection?.titleHighlight}
        subtitle={mainServicesData?.heroSection?.subtitle}
        description={mainServicesData?.heroSection?.description}
        buttonText={mainServicesData?.heroSection?.buttonText}
        buttonLink={mainServicesData?.heroSection?.buttonLink}
        showThemeToggle={mainServicesData?.heroSection?.showThemeToggle}
        loaderProgress={loadingProgress}
        isLoading={isLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && (
        <div className='page-wrapper two-image-section flex column-gap-20'>
          <Link
            to='/services/b2c'
            className={`${!isMobileSize ? 'flex-1' : ''} link-reset`}
          >
            <BackgroundSection
              imageUrl={
                isDarkMode
                  ? mainServicesData?.servicesSection?.firstService
                      ?.darkModeImageUrl ||
                    mainServicesData?.servicesSection?.firstService
                      ?.lightModeImageUrl
                  : mainServicesData?.servicesSection?.firstService
                      ?.lightModeImageUrl
              }
              bgClassName='full-height flex flex-column justify-end flex-1'
              key='1'
              className='service-nav-links'
            >
              <p className='img-page-font-grotesk font-white-color align-self-center font-size-45 letter-spacing-4p'>
                {mainServicesData?.servicesSection?.firstService?.titleGrotesk}{' '}
                <span className='font-secondary'>
                  {mainServicesData?.servicesSection?.firstService?.titleItalic}
                </span>
              </p>
            </BackgroundSection>
          </Link>
          <Link
            to='/services/b2b'
            className={`${!isMobileSize ? 'flex-1' : ''} link-reset`}
          >
            <BackgroundSection
              imageUrl={
                isDarkMode
                  ? mainServicesData?.servicesSection?.secondService
                      ?.darkModeImageUrl ||
                    mainServicesData?.servicesSection?.secondService
                      ?.lightModeImageUrl
                  : mainServicesData?.servicesSection?.secondService
                      ?.lightModeImageUrl
              }
              bgClassName='full-height flex flex-column justify-end flex-1'
              key='2'
              className='service-nav-links'
            >
              <p className='img-page-font-grotesk font-white-color align-self-center font-size-45 letter-spacing-4p'>
                {mainServicesData?.servicesSection?.secondService?.titleGrotesk}{' '}
                <span className='font-secondary'>
                  {
                    mainServicesData?.servicesSection?.secondService
                      ?.titleItalic
                  }
                </span>
              </p>
            </BackgroundSection>
          </Link>
        </div>
      )}
      {!isLoading && mainServicesData?.ourRangeData?.items && (
        <RangeCarousel rangeCarouselData={mainServicesData.ourRangeData} />
      )}
      {!isLoading && mainServicesData?.fullPageImageData?.leftSideTitle && (
        <FullPageImageAnimation data={mainServicesData.fullPageImageData} />
      )}
      {!isLoading && getParagraphById(pID.P_2) && (
        <TextPage
          key={getParagraphById(pID.P_2)?._id}
          type={getParagraphById(pID.P_2)?.type}
          titleActive={getParagraphById(pID.P_2)?.titleActive}
          titleInactive={getParagraphById(pID.P_2)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_2)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_2)?.paragraphText}
          buttonText={getParagraphById(pID.P_2)?.buttonText}
          buttonLink={getParagraphById(pID.P_2)?.buttonLink}
        />
      )}
      {!isLoading && <Brochure data={mainServicesData?.brochureData} />}
    </PageLayout>
  );
};

export default MainServices;
